/**
 * saveSwap — applySaveData의 "필드 분배"를 번들 스왑으로 대체하는 순수 헬퍼 (Phaser-free).
 *
 * - 컨트롤러: decode 산출물(GameSaveState) 중 DomainControllers와 겹치는 키만 추출해
 *   `{ ...controllers, ...pickControllerSwap(s) }`로 통째 교체한다.
 * - 진행 셸 상태(ProgressState): 컨트롤러가 아닌 저장 위임 필드 묶음 — 동일하게 번들로 스왑.
 *
 * ⚠ 위치가 app/인 이유: SaveCodec(systems/)이 DomainControllers(app/createControllers)를 import하면
 * createControllers→SaveCodec(createGoldBox) 런타임 순환이 생긴다 — 게이트는 양쪽 타입을 안전하게
 * 볼 수 있는 app/에 둔다.
 */
import type { DomainControllers } from "./createControllers.js";
import type { GameSaveState } from "../systems/SaveCodec.js";
import type { CooldownEntry } from "@yjsgames/cooldown";

/** decode 산출물 중 controllers 번들로 스왑되는 키 (SSoT). */
export const CONTROLLER_SWAP_KEYS = [
  "chapter",
  "economy",
  "inventory",
  "playerModuleLevels",
  "moduleMastery",
  "moduleSynergy",
  "synergyLevels",
  "spaceship",
  "upgradeInv",
  "grid",
  "goldBox",
  "diamondBox",
  "rebirth",
  "statUpgrade",
  "rebirthUpgrade",
  "relic",
  "dungeon",
  "dailyShop",
  "fusion",
  "mysteryBlock",
  "guideQuest",
  "analyticsMilestones",
] as const satisfies readonly (keyof DomainControllers & keyof GameSaveState)[];

type SwapKey = (typeof CONTROLLER_SWAP_KEYS)[number];

/** 누락 게이트 — 저장되는 컨트롤러가 새로 생기면(두 타입 교집합에 키 추가) 여기 누락 시 컴파일 에러. */
type MissingSwapKey = Exclude<keyof DomainControllers & keyof GameSaveState, SwapKey>;
export const controllerSwapKeysExhaustive: [MissingSwapKey] extends [never]
  ? true
  : MissingSwapKey = true;

/** decode 산출물에서 controllers 스왑분 추출 — 키별 타입 동일성은 Pick 반환 타입이 강제. */
export function pickControllerSwap(s: GameSaveState): Pick<DomainControllers, SwapKey> {
  return {
    chapter: s.chapter,
    economy: s.economy,
    inventory: s.inventory,
    playerModuleLevels: s.playerModuleLevels,
    moduleMastery: s.moduleMastery,
    moduleSynergy: s.moduleSynergy,
    synergyLevels: s.synergyLevels,
    spaceship: s.spaceship,
    upgradeInv: s.upgradeInv,
    grid: s.grid,
    goldBox: s.goldBox,
    diamondBox: s.diamondBox,
    rebirth: s.rebirth,
    statUpgrade: s.statUpgrade,
    rebirthUpgrade: s.rebirthUpgrade,
    relic: s.relic,
    dungeon: s.dungeon,
    dailyShop: s.dailyShop,
    fusion: s.fusion,
    mysteryBlock: s.mysteryBlock,
    guideQuest: s.guideQuest,
    analyticsMilestones: s.analyticsMilestones,
  };
}

/** 저장계 진행 셸 상태 — 컨트롤러 외 SaveData 위임 필드 묶음 (GameScene.progress). */
export interface ProgressState {
  /** 일일 리셋 키 → 마지막 리셋 시각(epoch ms) — SaveData.dailyResets SSoT (in-place 변경 후 persist 직렬화). */
  dailyResets: Record<string, number>;
  discovered: Set<string>;
  effectLevel: number;
  /** 도감 마지막 열람 시점 bestChapter — 그 이후 새 해금 조각이 있으면 도감 탭 레드닷(codexRedDot). 도감 진입 시 갱신. */
  codexSeenChapter: number;
  /** 광고 제거 IAP 보유 entitlement 캐시 — true면 ad 슬롯 즉시 수령 + 강제광고 차단. */
  adFree: boolean;
  /** 쿨타임 entry 맵 — SaveData.cooldowns SSoT (CooldownService in-place 변경 후 persist 직렬화, dailyResets 동형). */
  cooldowns: Record<string, CooldownEntry>;
  /** 오늘의 광고 다이아 수령 여부 (자정 리셋 시 false). */
  shopDailyAdDiamondClaimed: boolean;
  /** 완료 튜토리얼 시나리오 id — SaveDataTutorialStore가 in-place 갱신(참조 공유 규약). */
  tutorialCompleted: string[];
  /** 튜토리얼 재개 스텝 인덱스 맵 — 참조 공유 규약 동일. */
  tutorialResume: Record<string, number>;
  /** 해금 탭 레드닷을 소비한 잠금 탭 키 — in-place push가 persist에 반영(tutorialCompleted 참조 공유 동형). */
  tabUnlockSeen: string[];
  /** 읽음 처리한 선택형 다이어로그 id — in-place push가 persist에 반영. */
  dialogueSeenIds: string[];
}

/** 새 게임 초기 진행 상태 (저장 없을 때 — applySaveData 미호출 경로). */
export function createInitialProgress(): ProgressState {
  return {
    dailyResets: {},
    discovered: new Set(),
    effectLevel: 1,
    codexSeenChapter: 1,
    adFree: false,
    cooldowns: {},
    shopDailyAdDiamondClaimed: false,
    tutorialCompleted: [],
    tutorialResume: {},
    tabUnlockSeen: [],
    dialogueSeenIds: [],
  };
}

/**
 * decode 산출물 → ProgressState. dailyResets/tutorial*은 **참조 공유**(in-place 갱신이 persist 직렬화에
 * 반영 — SaveCodec decode의 참조 전달 규약과 한 쌍). discovered만 Set으로 변환(원본은 배열).
 */
export function progressFromSave(s: GameSaveState): ProgressState {
  return {
    dailyResets: s.dailyResets,
    discovered: new Set(s.discovered),
    effectLevel: s.effectLevel,
    codexSeenChapter: s.codexSeenChapter,
    adFree: s.adFree,
    cooldowns: s.cooldowns,
    shopDailyAdDiamondClaimed: s.shopDailyAdDiamondClaimed,
    tutorialCompleted: s.tutorialCompleted,
    tutorialResume: s.tutorialResume,
    tabUnlockSeen: s.tabUnlockSeen,
    dialogueSeenIds: s.dialogueSeenIds,
  };
}
